<template>
  <basic-dialog :show="show" @close="$emit('close')" max-width="99%" draggable>
    <template v-slot:title>Batch Receipts Create</template>
    <v-card>
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <v-text-field
              label="Lodgement Ref"
              v-model="lodgementRef"
              :outlined="outlinedInputs"
              hide-details
              dense
            ></v-text-field>
          </v-col>
          <v-col>
            <payment-type-select
              v-model="paymentType"
              :outlined="outlinedInputs"
              hide-details
              dense
            ></payment-type-select>
          </v-col>
          <v-col>
            <date-picker
              customLabel="Lodgement Date"
              @onDateSelected="onDateSelected"
              :gtDateString="lodgementDate"
              :outlined="outlinedInputs"
              hide-details
              dense
            ></date-picker>
          </v-col>
          <v-col>
            <v-autocomplete
              label="Currency"
              :outlined="outlinedInputs"
              v-model="currencyCode"
              :items="currencyList"
              item-text="name"
              item-value="code"
              @change="onCurrencyChange"
              hide-details
              dense
            ></v-autocomplete>
          </v-col>
          <v-col>
            <v-text-field
              label="Exchange Rate"
              v-model:number="exchangeRate"
              :outlined="outlinedInputs"
              hide-details
              dense
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              label="Payer Name"
              v-model="payerName"
              :outlined="outlinedInputs"
              hide-details
              dense
            ></v-text-field>
          </v-col>
          <v-col>
            <v-text-field
              label="Payer Surname"
              v-model="payerSurname"
              :outlined="outlinedInputs"
              hide-details
              dense
            ></v-text-field>
          </v-col>
          <v-col v-if="showPaymentMethods">
            <payment-method-list-select
              v-model="paymentMethodId"
              :outlined="outlinedInputs"
              hide-details
              dense
            ></payment-method-list-select>
          </v-col>
          <v-col>
            <v-text-field
              label="Notes"
              v-model="notes"
              :outlined="outlinedInputs"
              hide-details
              dense
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row>
      <v-col>
        <v-divider></v-divider>
      </v-col>
    </v-row>
    <v-card>
      <v-card-text>
        <itinerary-list
          :cached-filters="true"
          show-filters
          :show-title="false"
          show-filters-label
          :filter-cols="2"
          ref-name="batchCreateItineraryList"
          ref-name-columns="batchReceiptsItineraryCols"
          hide-ag-grid
          hide-create
          hide-actions
          :height="400"
          :custom-columns="itiColumns"
          :additional-headers="itiAdditonalHeaders"
          editable
          @select="onItinerarySelect"
          @loaded="onItinerariseLoaded"
          ref-label-new-tab
        ></itinerary-list> </v-card-text
    ></v-card>
    <v-row v-if="debug">
      <v-col>
        <code>{{ selectedItineraries }}</code>
      </v-col>
    </v-row>

    <template v-slot:actions-left>
      <h2 class="title my-0 py-0 mr-2" color="teal darken-3">
        {{ selectedItineraries.length }} record(s), Total: {{ total }}
      </h2>
    </template>
    <template v-slot:actions>
      <v-btn
        v-if="$store.getters.hasAccessTo('finances:xero_export')"
        @click="onCreateAndExportToXero"
        :disabled="!isValid || loading"
        :loading="loading"
        >Create &amp; Export to Xero</v-btn
      >
      <v-btn
        color="primary"
        :disabled="!isValid || loading"
        @click="onCreate(false)"
        :loading="loading"
        >Create</v-btn
      >
    </template>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../shared/BasicDialog.vue";
import { ALL_CURRENCIES_SEARCH_QUERY } from "../../../../constants/graphql";
import { removeGQLTypename } from "../../../../helpers";
import PaymentTypeSelect from "../../shared/PaymentTypeSelect.vue";
import DatePicker from "../../shared/DatePicker.vue";
import ItineraryList from "../../itineraries/ItineraryList.vue";
import TaskStatusMixin from "../../../../mixins/TaskStatusMixin";
import gql from "graphql-tag";
import EventBus from "../../../../store/event-bus";
import PaymentMethodListSelect from "../../shared/PaymentMethodListSelect.vue";
const CREATE_ITINERARY_PAYMENTS = gql`
  mutation ($input: ItineraryPaymentsInput) {
    createItineraryPayments(input: $input) {
      id
    }
  }
`;
export default {
  name: "batch-create-with-filters",

  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
  },

  mixins: [TaskStatusMixin],

  components: {
    BasicDialog,
    PaymentTypeSelect,
    DatePicker,
    ItineraryList,
    PaymentMethodListSelect,
  },

  data() {
    return {
      debug: false,
      lodgementRef: "",
      lodgementDate: new Date().toISOString(false),
      currencyCode: "",
      exchangeRate: 1.0,
      payerName: "",
      payerSurname: "",
      paymentType: "",
      notes: "",
      loading: false,
      itiColumns: [
        "startDate",
        "reference",
        "agentName",
        "totalPrice",
        "balance",
        "amount",
        "paymentCurrency",
      ],
      itiAdditonalHeaders: [
        {
          text: "Amount",
          value: "amount",
          sortable: true,
          name: "amount",
        },
        {
          text: "Receipt Currency",
          value: "paymentCurrency",
          sortable: true,
          name: "paymentCurrency",
        },
      ],
      selectedItineraries: [],
      paymentMethodId: "",
    };
  },

  computed: {
    showPaymentMethods() {
      return this.$store.getters.hasAccessTo("finances:payment_methods");
    },
    isValid() {
      return (
        this.selectedItineraries.length > 0 &&
        this.currencyCode != "" &&
        this.paymentType != "" &&
        this.lodgementRef != ""
      );
    },
    total() {
      return this.selectedItineraries
        .reduce((t, item) => t + Number(item.amount), 0)
        .toFixed(2);
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    currencyList() {
      //   const list = this.itineraries this.itineraries.reduce((l, item) => {
      //     if (l.indexOf(item.currencyCode) == -1) {
      //       l.push(item.currencyCode);
      //     }
      //     return l;
      //   }, []);
      //   return list;
      return this.currencies
        ? this.currencies.edges.map(removeGQLTypename)
        : [];
    },
  },

  methods: {
    taskStatusCompleteHandler(data) {
      //console.log("task completed: ", data);
      this.loading = false;
      this.$store.dispatch("showAppSnackbar", {
        msg: data.taskStatus.notes,
      });
      this.reset();
      this.$emit("complete");
      EventBus.$emit("reloadItineraryList");
    },
    reset() {
      this.lodgementRef = "";
      this.notes = "";
      this.payerName = "";
      this.payerSurname = "";
    },
    onCreateAndExportToXero() {
      this.onCreate(true);
    },
    onCreate(exportToXero = false) {
      const input = {
        lodgementRef: this.lodgementRef,
        notes: this.notes,
        lodgementDate: this.lodgementDate,
        paymentType: this.paymentType,
        payerName: this.payerName,
        payerSurname: this.payerSurname,
        paymentMethodId: this.paymentMethodId,
        currencyCode: this.currencyCode,
        exchangeRate: Number(this.exchangeRate),
        totalAmount: Number(this.total),
        itineraries: this.selectedItineraries.map((item) => ({
          id: item.id,
          amount: Number(item.amount),
        })),
        exportToXero: exportToXero,
      };
      //console.log(input);
      this.$apollo
        .mutate({
          mutation: CREATE_ITINERARY_PAYMENTS,
          variables: { input },
        })
        .then(({ data }) => {
          //console.log(data.createSupplierPayments.id);
          this.loading = true;
          this.taskID = data.createItineraryPayments.id;
          this.checkStatus = true;
        })
        .catch((err) => {
          this.loading = false;
          console.error(err);
        });
    },
    onItinerarySelect(payload) {
      this.selectedItineraries = [...payload];
    },
    onCurrencyChange(payload) {
      if (this.currencies && this.currencies.edges) {
        const currency = this.currencies.edges.find(
          (c) => c.node.code == payload
        );
        if (currency) {
          this.exchangeRate = currency.node.exchangeRate;
        }
      }
      EventBus.$emit("updateItinerarListItems", (item) => ({
        ...item,
        paymentCurrency: payload,
      }));
    },
    onItinerariseLoaded() {
      EventBus.$emit("updateItinerarListItems", (item) => ({
        ...item,
        paymentCurrency: this.currencyCode,
      }));
    },
    onDateSelected(payload) {
      this.lodgementDate = payload;
    },
  },

  apollo: {
    currencies: {
      query: ALL_CURRENCIES_SEARCH_QUERY,
      variables() {
        return {
          includeExchangeRate: true,
        };
      },
      skip() {
        return !this.show;
      },
      result() {
        if (this.currencyCode != "") {
          this.onCurrencyChange(this.currencyCode);
        }
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/finances/payments/BatchCreateWithFilters.vue